import { CHART_SHARE_CALCULATION_TYPE } from '@/entities/chart/model/constants';
import type { ChartConfig, TotalsMap } from '@/features/(charts)/chart-renderer/ui/Cross/types';
import { getPercentStringFormat } from '@/utils/util';

/**
 * Total(합계) 값 계산을 담당하는 클래스
 */
export class TotalCalculator {
  /**
   * Row Total(행 합계) 값 계산
   *
   * @param config - 차트 설정
   * @param rowTotal - 행의 합계 값
   * @param grandTotal - 전체 합계 값
   * @returns 계산된 행 합계 값
   */
  static calculateRowTotalValue(
    config: ChartConfig,
    rowTotal: number,
    grandTotal: number,
  ): number {
    if (!config.isPercentage) return rowTotal;

    if (
      config.shareCalculationType === CHART_SHARE_CALCULATION_TYPE.GRAND_TOTAL
    ) {
      return Number(getPercentStringFormat(grandTotal, rowTotal, 2));
    }

    return 100; // Default for other calculation types
  }

  /**
   * Column Total(열 합계) 값 계산
   *
   * @param config - 차트 설정
   * @param columnTotal - 열의 합계 값
   * @param grandTotal - 전체 합계 값
   * @returns 계산된 열 합계 값
   */
  static calculateColumnTotalValue(
    config: ChartConfig,
    columnTotal: number,
    grandTotal: number,
  ): number {
    if (config.isCellSentimentCalculation) {
      // Handle sentiment calculation separately
      return columnTotal;
    }

    if (!config.isPercentage) return Number(columnTotal.toFixed(1));

    if (
      config.shareCalculationType === CHART_SHARE_CALCULATION_TYPE.GRAND_TOTAL
    ) {
      return Number(getPercentStringFormat(grandTotal, columnTotal, 2));
    }

    if (config.shareCalculationType === CHART_SHARE_CALCULATION_TYPE.COLUMN) {
      return 100;
    }

    return Number(getPercentStringFormat(columnTotal, columnTotal, 2));
  }

  /**
   * Grand Total(전체 합계) 값 계산
   *
   * @param config - 차트 설정
   * @param grandTotal - 전체 합계 값
   * @returns 계산된 전체 합계 값
   */
  static calculateGrandTotalValue(
    config: ChartConfig,
    grandTotal: number,
  ): number {
    if (!config.isPercentage) return grandTotal;
    return 100; // Always 100% for percentage view
  }

  /**
   * 퍼센트 계산을 위한 기준 total 값 반환
   *
   * @param shareCalculationType - 공유 계산 타입
   * @param xValue - x축 값
   * @param yValue - y축 값
   * @param totals - total 맵
   * @returns 퍼센트 계산 기준 값
   */
  static getTotalForPercentage(
    shareCalculationType: CHART_SHARE_CALCULATION_TYPE,
    xValue: StringNumberValue,
    yValue: StringNumberValue,
    totals: TotalsMap,
  ): number {
    switch (shareCalculationType) {
      case CHART_SHARE_CALCULATION_TYPE.ROW:
        return totals.xTotalMap[xValue];
      case CHART_SHARE_CALCULATION_TYPE.GRAND_TOTAL:
        return totals.grandTotal;
      case CHART_SHARE_CALCULATION_TYPE.COLUMN:
      default:
        return totals.yTotalMap[yValue];
    }
  }
}
